// Funções puras compartilhadas pelos leitores de extrato (CSV e OFX).
// Sem React, sem Supabase: dá para testar no Jest sem nada montado.

// Texto para comparação: sem acento, minúsculo, espaços colapsados.
export function normalizarTexto(s) {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function dataValida(ano, mes, dia) {
  const y = Number(ano); const m = Number(mes); const d = Number(dia);
  if (!y || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// Aceita dd/mm/aaaa, dd/mm/aa, dd-mm-aaaa, dd.mm.aaaa, aaaa-mm-dd e aaaammdd
// (com ou sem hora depois). Devolve 'AAAA-MM-DD' ou null.
export function parseData(valor) {
  const s = String(valor ?? '').trim();
  let m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})(?:\D|$)/);
  if (m) return dataValida(m[3], m[2], m[1]);
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2})(?:\D|$)/);
  if (m) return dataValida(`20${m[3]}`, m[2], m[1]);
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return dataValida(m[1], m[2], m[3]);
  m = s.match(/^(\d{4})(\d{2})(\d{2})/);
  if (m) return dataValida(m[1], m[2], m[3]);
  return null;
}

// Converte o valor do extrato em número com sinal.
//   '1.234,56' → 1234.56   '-150,00' → -150   '1,234.56' → 1234.56
//   '(80,00)' → -80        '80,00 D' → -80    'R$ 10' → 10
// `milharComPonto`: em arquivo com ';' (padrão brasileiro) '1.500' é mil e
// quinhentos; em arquivo com ',' (padrão americano) '1.500' é um e meio.
export function parseValor(valor, { milharComPonto = true } = {}) {
  let s = String(valor ?? '').trim();
  if (!s) return NaN;
  let negativo = false;
  if (/^\(.*\)$/.test(s)) { negativo = true; s = s.slice(1, -1); }
  if (/\s*D$/i.test(s)) { negativo = true; s = s.replace(/\s*D$/i, ''); }
  else if (/\s*C$/i.test(s)) { s = s.replace(/\s*C$/i, ''); }
  s = s.replace(/R\$/gi, '').replace(/\s/g, '');
  if (s.endsWith('-')) { negativo = !negativo; s = s.slice(0, -1); }
  if (s.startsWith('-')) { negativo = !negativo; s = s.slice(1); }
  else if (s.startsWith('+')) { s = s.slice(1); }
  if (!/^[\d.,]+$/.test(s)) return NaN;

  const ultimoPonto = s.lastIndexOf('.');
  const ultimaVirgula = s.lastIndexOf(',');
  if (ultimoPonto >= 0 && ultimaVirgula >= 0) {
    // O separador que aparece por último é o decimal.
    s = ultimaVirgula > ultimoPonto
      ? s.replace(/\./g, '').replace(',', '.')
      : s.replace(/,/g, '');
  } else if (ultimaVirgula >= 0) {
    s = s.replace(',', '.');
  } else if (ultimoPonto >= 0 && milharComPonto && /^\d{1,3}(\.\d{3})+$/.test(s)) {
    s = s.replace(/\./g, '');
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return NaN;
  return negativo ? -n : n;
}

// Hash estável (cyrb53) em hexadecimal. Duas sementes = 106 bits, folga de sobra
// para a chave de deduplicação de uma empresa.
function cyrb53(str, seed = 0) {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, '0');
}

export function hashEstavel(str) {
  return cyrb53(str, 0) + cyrb53(str, 1);
}

// Código do banco (COMPE) → nome. Usado pelo OFX (BANKID) e para sugerir a conta.
export const BANCOS_POR_CODIGO = {
  '1': 'Banco do Brasil',
  '33': 'Banco Santander',
  '41': 'Banrisul',
  '77': 'Banco Inter',
  '104': 'Caixa Econômica Federal',
  '208': 'BTG Pactual',
  '237': 'Bradesco',
  '260': 'Nubank',
  '290': 'PagBank',
  '323': 'Mercado Pago',
  '336': 'C6 Bank',
  '341': 'Itaú',
  '380': 'PicPay',
  '403': 'Cora',
  '748': 'Sicredi',
  '756': 'Sicoob',
};

export function nomeBancoPorCodigo(codigo) {
  const limpo = String(codigo ?? '').replace(/\D/g, '').replace(/^0+/, '');
  return BANCOS_POR_CODIGO[limpo] || null;
}
